import apiClient from "./axios";
import dayjs from "dayjs";
import { getAirportMinGroundTime } from "../utils/minGroundTimeUtils";

/**
 * 航班智能规划API
 * 基于DFS+剪枝算法的智能航班规划服务
 */
export const flightPlannerApi = {
    /**
     * 执行航班规划
     * @param {Object} planningData 规划参数
     * @param {string} planningData.start_date 开始日期 (YYYY-MM-DD)
     * @param {string} planningData.end_date 结束日期 (YYYY-MM-DD)
     * @param {string} planningData.aircraft_reg 飞机注册号
     * @param {string} planningData.initial_location 初始位置机场代码
     * @param {Array} planningData.sales_routes 销售航线列表
     * @param {Array} planningData.route_chains 航线链列表
     * @param {number} planningData.max_search_time 最大搜索时间（秒）
     * @returns {Promise<Object>} 规划结果
     */
    planFlights: async (planningData) => {
        try {
            const response = await apiClient.post("/v1/flight-planner/plan", planningData, {
                timeout: 120000, // 2分钟超时
            });
            return response;
        } catch (error) {
            console.error("航班规划失败:", error);
            throw error;
        }
    },

    /**
     * 获取航班规划器状态
     * @returns {Promise<Object>} 规划器状态信息
     */
    getPlannerStatus: async () => {
        try {
            const response = await apiClient.get("/v1/flight-planner/status");
            return response;
        } catch (error) {
            console.error("获取规划器状态失败:", error);
            throw error;
        }
    },

    /**
     * 获取可用的机场列表
     * @returns {Promise<Object>} 机场列表
     */
    getAvailableAirports: async () => {
        try {
            const response = await apiClient.get("/v1/flight-planner/available-airports");
            return response;
        } catch (error) {
            console.error("获取机场列表失败:", error);
            throw error;
        }
    },

    /**
     * 获取可用的航段列表
     * @returns {Promise<Object>} 航段列表
     */
    getAvailableLegs: async () => {
        try {
            const response = await apiClient.get("/v1/flight-planner/available-legs");
            return response;
        } catch (error) {
            console.error("获取航段列表失败:", error);
            throw error;
        }
    },
};

/**
 * 计算时间范围内的周数
 * @param {string} startDate 开始日期
 * @param {string} endDate 结束日期
 * @returns {number} 周数
 */
const calculateWeeksInRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.ceil(diffDays / 7)); // 至少1周
};

/**
 * 根据频率和时间范围扩展运输需求
 * @param {Array} originalDemands 原始运输需求
 * @param {number} weeksInRange 时间范围内的周数
 * @param {number} weeklyFrequency 每周期望频率
 * @returns {Array} 扩展后的运输需求
 */
const expandTransportDemands = (originalDemands, weeksInRange, weeklyFrequency = 5) => {
    const expandedDemands = [];

    originalDemands.forEach((demand) => {
        // 计算该需求在时间范围内应该生成的总次数
        const demandFrequency = demand.frequency || weeklyFrequency;
        const totalInstances = Math.ceil(weeksInRange * demandFrequency);

        // 为每个运输需求生成多个实例
        for (let i = 0; i < totalInstances; i++) {
            expandedDemands.push({
                origin: demand.origin,
                destination: demand.destination,
                frequency: 1, // 每个实例的频率都是1
                preferred_time: demand.preferred_time,
                value: demand.value || 1.0,
            });
        }
    });

    return expandedDemands;
};

/**
 * 将ScheduleEditor的数据转换为航班规划请求格式
 * @param {Object} scheduleData ScheduleEditor中的计划数据
 * @param {Object} options 可选参数
 * @returns {Object} 航班规划请求数据
 */
export const convertScheduleDataToPlanningRequest = (scheduleData, options = {}) => {
    const {
        aircraft_reg = scheduleData?.aircraft_reg || "DEFAULT_AIRCRAFT",
        initial_location = "HKG",
        iterations = 1000,
        weekly_frequency = 5, // 每周期望频率，默认5班
        ...otherOptions
    } = options;

    // 计算时间范围内的周数
    const weeksInRange = calculateWeeksInRange(scheduleData.start_date, scheduleData.end_date);

    // 获取原始运输需求
    const originalDemands = scheduleData.parameters?.transportation_demands || [];

    // 根据频率和时间范围扩展运输需求
    const transport_demands = expandTransportDemands(originalDemands, weeksInRange, weekly_frequency);

    console.log(`时间范围: ${weeksInRange} 周, 每周期望频率: ${weekly_frequency}, 原始需求: ${originalDemands.length}, 扩展后需求: ${transport_demands.length}`);

    // 转换宵禁约束
    const curfew_constraints = (scheduleData.parameters?.curfew_constraints || []).map((constraint) => ({
        airport_code: constraint.airport_code,
        start_time: constraint.start_time,
        end_time: constraint.end_time,
        days_of_week: constraint.days_of_week,
    }));

    // 构建过站成本 - 参考测试文件设置默认值
    const defaultTurnaroundCosts = {
        "NQZ": 1000,  // NQZ的过站成本非常高
        "LGG": 50,    // LGG则相对便宜
        "BUD": 1000,
        "HKG": 2000,
        "HGH": 2000,
        "CGO": 1500,
        "DWC": 1200,
        "SHJ": 1200,
        "MAD": 800,
        "HHN": 300,
    };
    
    // 合并用户设置的过站成本和默认值
    const turnaround_costs = {
        ...defaultTurnaroundCosts,
        ...(scheduleData.parameters?.turnaround_costs || {}),
    };

    return {
        start_date: scheduleData.start_date,
        end_date: scheduleData.end_date,
        aircraft_reg,
        initial_location,
        transport_demands,
        curfew_constraints,
        min_ground_time: scheduleData.parameters?.min_ground_time || 60,
        iterations,
        turnaround_costs,
        ...otherOptions,
    };
};

/**
 * 将航班规划结果转换为ScheduleEditor可用的航班格式
 * @param {Object} planningResult 航班规划结果
 * @param {Object} scheduleData 原始计划数据
 * @returns {Array} 转换后的航班列表
 */
export const convertPlanningResultToFlights = (planningResult, scheduleData) => {
    if (!planningResult.success || !planningResult.activities) {
        return [];
    }

    // 获取当前计划的基本信息
    const currentAircraftReg = scheduleData.aircraft?.registration || scheduleData.aircraft_reg || "";
    const scheduleId = scheduleData.id;

    // 获取机场约束（最小过站只认机场主数据的明确配置，不用计划参数兜底）
    const airportConstraints = scheduleData.constraints?.airportConstraints || {};

    // 预处理航段数据
    const flightLegsMap = (window.cachedFlightLegs || []).reduce((map, leg) => {
        const dep = leg.departure_airport || "UNKNOWN_DEP";
        const arr = leg.arrival_airport || "UNKNOWN_ARR";
        map[`${dep}-${arr}`] = leg;
        return map;
    }, {});

    return planningResult.activities.map((activity, index) => {
        // 解析时间字符串，确保使用UTC
        const departureTime = dayjs.utc(activity.departure_time);
        const arrivalTime = dayjs.utc(activity.arrival_time);

        // 提取日期部分
        const flightDate = departureTime.isValid() ? departureTime.format("YYYY-MM-DD") : null;

        // 查询匹配的航段
        const legKey = `${activity.departure_airport}-${activity.arrival_airport}`;
        const matchedLeg = flightLegsMap[legKey];

        // 设置最小轮档时间
        const matchedBlockTime = matchedLeg?.block_time_minutes ? parseInt(matchedLeg.block_time_minutes) : NaN;
        const minBlockTimeMinutes = !isNaN(matchedBlockTime) ? matchedBlockTime : 0;

        // 设置最小地面时间：只认到达机场主数据的明确配置（含 0 分钟）；
        // 机场没配就留空，不得用计划参数的 60 分钟兜底
        const minGroundTimeMinutes = getAirportMinGroundTime(airportConstraints, activity.arrival_airport);

        // 计算轮档时间
        const blockTimeMinutes = departureTime.isValid() && arrivalTime.isValid() ? arrivalTime.diff(departureTime, "minute") : minBlockTimeMinutes;

        return {
            // 基本信息
            id: `planned-${activity.activity_id}`,
            schedule_id: scheduleId,
            flight_number: activity.flight_number,
            flight_date: flightDate,

            // 机场信息
            departure_airport: activity.departure_airport,
            arrival_airport: activity.arrival_airport,

            // 时间信息 (使用ISO格式确保时区一致性)
            std: departureTime.isValid() ? departureTime.format("YYYY-MM-DDTHH:mm:ss") + "Z" : null,
            sta: arrivalTime.isValid() ? arrivalTime.format("YYYY-MM-DDTHH:mm:ss") + "Z" : null,

            // 飞机信息
            aircraft_reg: currentAircraftReg, // 使用当前计划的飞机注册号

            // 时间约束
            min_block_time_minutes: minBlockTimeMinutes,
            min_ground_time_minutes: minGroundTimeMinutes,
            block_time_minutes: blockTimeMinutes,

            // 星期几
            day_of_week: departureTime.isValid() ? departureTime.day() || 7 : null, // 0 (周日) -> 7

            // 状态信息
            status: "PLANNED", // 标记为规划生成的航班

            // 成本信息
            cost: activity.cost,

            // 关联的需求ID
            demand_id: activity.demand_id,

            // 显示顺序
            display_order: index + 1,

            // 标记为规划生成
            is_planned: true,
            planning_source: "ALNS",
            isNew: true, // 标记为新创建
            isMassUpdate: true, // 标记为批量更新
        };
    });
};

/**
 * 合并规划结果到现有航班列表
 * @param {Array} existingFlights 现有航班列表
 * @param {Array} plannedFlights 规划生成的航班列表
 * @param {Object} options 合并选项
 * @returns {Array} 合并后的航班列表
 */
export const mergePlannedFlights = (existingFlights, plannedFlights, options = {}) => {
    const { replaceExisting = false, preserveManualFlights = true } = options;

    if (replaceExisting) {
        // 替换模式：保留手动创建的航班，替换规划生成的航班
        const manualFlights = preserveManualFlights ? existingFlights.filter((flight) => !flight.is_planned) : [];

        return [...manualFlights, ...plannedFlights];
    } else {
        // 追加模式：在现有航班基础上追加规划结果
        return [...existingFlights, ...plannedFlights];
    }
};

export default flightPlannerApi;
